import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Cluster,
  EmptyState,
  Field,
  Grid,
  Input,
  Modal,
  Page,
  QueryState,
  Stack,
} from '@integra/ui';
import { useCreateWorkspace, useWorkspaces } from '../api/hooks.js';

/**
 * Workspace picker (§5.1). A Workspace is the data isolation boundary, so it is
 * the first thing an operator chooses — every other screen is scoped beneath it.
 *
 * Data access is exclusively through hooks (§21.4); this component issues no
 * requests of its own.
 */
export function WorkspacesPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useWorkspaces();
  const createWorkspace = useCreateWorkspace();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');

  const submit = (): void => {
    createWorkspace.mutate(
      { name, slug },
      {
        // `useCreateWorkspace` refreshes the session in its own onSuccess — which
        // TanStack awaits before this one — so by the time we navigate, the access
        // token already carries the new workspace's OWNER grant (§14.1). Without
        // that refresh the first scoped action in the new workspace would 403,
        // since the token was minted before the workspace existed.
        onSuccess: (workspace) => {
          setOpen(false);
          setName('');
          setSlug('');
          navigate(`/w/${workspace.id}/dashboard`);
        },
      },
    );
  };

  return (
    <Page
      title="Workspaces"
      description="A Workspace is a client or project context, and the boundary your data is isolated within."
      actions={<Button onClick={() => setOpen(true)}>New workspace</Button>}
    >
      <QueryState loading={isLoading} error={error} what="workspaces">
        {data !== undefined && data.data.length === 0 ? (
          <EmptyState
            title="No workspaces yet"
            description="Create one for your first client or project. Its assets, campaigns, and channels all live inside it, isolated from every other workspace."
            action={<Button onClick={() => setOpen(true)}>Create a workspace</Button>}
          />
        ) : (
          <Grid min="md">
            {data?.data.map((workspace) => (
              <Card key={workspace.id} title={workspace.name} subtitle={`/${workspace.slug}`}>
                <Stack gap="sm">
                  <p className="font-mono text-[11px] text-ink-muted">{workspace.id}</p>
                  <Cluster gap="sm">
                    <Button size="sm" onClick={() => navigate(`/w/${workspace.id}/dashboard`)}>
                      Open
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => navigate(`/w/${workspace.id}/campaigns`)}
                    >
                      Campaigns
                    </Button>
                  </Cluster>
                </Stack>
              </Card>
            ))}
          </Grid>
        )}
      </QueryState>

      <Modal
        open={open}
        title="New workspace"
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} loading={createWorkspace.isPending}>
              Create
            </Button>
          </>
        }
      >
        <Stack gap="md">
          <Field label="Name" htmlFor="ws-name">
            <Input
              id="ws-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                // Suggest a slug, but leave it editable.
                setSlug(
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-+|-+$/g, '')
                    .slice(0, 63),
                );
              }}
              placeholder="Client or project name"
            />
          </Field>
          <Field
            label="Slug"
            htmlFor="ws-slug"
            hint="Lowercase letters, numbers and hyphens."
            error={
              createWorkspace.error instanceof Error ? createWorkspace.error.message : undefined
            }
          >
            <Input id="ws-slug" value={slug} onChange={(e) => setSlug(e.target.value)} />
          </Field>
        </Stack>
      </Modal>
    </Page>
  );
}
